import type { Configuration, RedirectRequest, SilentRequest } from '@azure/msal-browser'
import { LogLevel } from '@azure/msal-browser'

/**
 * MSAL configuration — Microsoft Entra External ID for Customers (CIAM).
 *
 * Reglas (hivanexus-frontend/CLAUDE.md):
 *   - Tokens en `sessionStorage`, NUNCA `localStorage`.
 *   - knownAuthorities OBLIGATORIO para CIAM — el dominio *.ciamlogin.com
 *     no está en la lista default de autoridades confiables de MSAL.
 */

const env = import.meta.env

const clientId = env.VITE_ENTRA_SPA_CLIENT_ID ?? ''
const apiAppId = env.VITE_ENTRA_API_APP_ID ?? ''
const authority = env.VITE_ENTRA_AUTHORITY ?? ''

// Para External ID hay que registrar el host de la autoridad como conocido.
// Si la authority es `https://hivanexusdev.ciamlogin.com/<guid>`, el host es
// `hivanexusdev.ciamlogin.com`.
const authorityHost = (() => {
  try {
    return authority ? new URL(authority).host : ''
  } catch {
    return ''
  }
})()

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    knownAuthorities: authorityHost ? [authorityHost] : [],
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // sessionStorage: tokens scoped a la pestaña, se limpian al cerrarla.
    // Cumple la regla "no localStorage" del CLAUDE.md (no persisten entre
    // sesiones del browser) y es compatible con redirect flow en MSAL v5+.
    //
    // Por qué NO `memoryStorage`: en MSAL Browser v5 redirect requiere
    // storage persistente entre el roundtrip a Entra (memoryStorage se pierde
    // al recargar la página). El error que tira es
    // BrowserConfigurationAuthError: in_mem_redirect_unavailable.
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        if (level === LogLevel.Error) console.error('[msal]', message)
        else if (level === LogLevel.Warning) console.warn('[msal]', message)
      },
    },
  },
}

/**
 * Scopes requeridos al hacer login. Incluyen el scope custom del API para que
 * Entra registre el consent una sola vez (después `acquireTokenSilent` no
 * vuelve a pedir consent al usuario).
 */
export const loginRequest: RedirectRequest = {
  scopes: apiAppId
    ? [`api://${apiAppId}/access_as_user`, 'openid', 'profile', 'email']
    : ['openid', 'profile', 'email'],
}

/**
 * Scopes para adquirir un access token destinado a llamar a hivanexus-api.
 * El backend valida `aud === apiAppId`, por eso el scope es `api://.../...`.
 */
export const apiTokenRequest: SilentRequest = {
  scopes: apiAppId ? [`api://${apiAppId}/access_as_user`] : [],
}

export const isMsalConfigured = Boolean(clientId && authority && apiAppId)
